import { useState } from 'react';
import useSWR from 'swr';
import { useNavigate } from 'react-router-dom';
import {
 Heading,
 Box,
 Container,
 Tabs,
 TabPanels,
 TabPanel,
 useToast,
 HStack,
} from '@chakra-ui/react';
import { InputText, Select, YearPickerInput } from '../../../components/Input';
import { QuestionnaireHeaderNew } from '../../../components/Form';
import PillarForm from './PillarForm';
import { fetchData } from '../../../services/fetch';
import baseService from '../../../services/base';
import { SuccessModal } from '../../../components/Modals';
import { optionsNumPilares } from '../../../resources/mock_options';
import { useDiagnosisForm } from '../../../hooks/useDiagnosisForm';
import TabList from '../../../components/Certification/TabList/TabList';
import { PillarConfigList } from '../../../components/Certification/PillarConfig/PillarConfigList';

export default function DiagnosisRegister() {
 const toast = useToast();
 const navigate = useNavigate();

 const [activeTab, setActiveTab] = useState(0);
 const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
 const [exercise, setExercise] = useState(null);

 const {
  showPillarsTabs,
  setShowPillarsTabs,
  pillarCount,
  formMethods,
  fields,
  append,
  remove,
  handleNumPilaresChange,
 } = useDiagnosisForm({
  isEditing: false,
 });

 const {
  handleSubmit,
  register,
  control,
  setValue,
  trigger,
  formState: { errors, isValid, isDirty, isSubmitting },
 } = formMethods;

 const { data: templates, isLoading: templatesIsLoading } = useSWR(
  showPillarsTabs ? `/survey-template/all` : null,
  fetchData
 );

 const handleTabsChange = (index) => {
  setActiveTab(index);
 };

 const showErrorToast = (message) => {
  toast({
   title: 'Erro',
   description: message,
   status: 'error',
  });
 };

 function handlePillarsConfig() {
  setShowPillarsTabs(true);

  append({
   idPillar: null,
   numberOfThemes: '',
   numberOfOptions: '',
   invisible: true,
  });

  setTimeout(() => {
   remove(fields?.length);
   setActiveTab(1);
  }, 1000);
 }

 const onSubmit = async (data) => {
  try {
   const surveyResponse = await baseService.post(`/survey/`, {
    description: data.name,
    surveyQuestions: [],
    type: 'diagnostic',
    ...(exercise ? { exercise: exercise.getFullYear() } : {}),
   });

   const idSurvey = surveyResponse?.data?.id_survey;

   // EG-40: o pilar chega SELECIONADO do catálogo global. Nome, texto,
   // imagem e helpers saíram do payload — são conteúdo global, editado em
   // /pillars. Os temas também não são criados aqui: pertencem ao pilar.
   const postPillar = async (pillarData) => {
    const { idPillar, levels } = pillarData;

    const transformedLevels = levels.map((level) => ({
     nivel: level.nivel,
     questions: level.questions.map((question) => question.id_question_template),
    }));

    const formValues = {
     id_pillar: idPillar,
     niveis: transformedLevels,
    };

    await baseService.post(
     `/survey/diagnostic/pillar`,
     JSON.stringify({
      id_survey: idSurvey,
      ...formValues,
     })
    );
   };

   await Promise.all(data.form.map(postPillar));
   setIsSuccessModalOpen(true);
  } catch (error) {
   showErrorToast('Não foi possível salvar o questionário');
  }
 };

 return (
  <form onSubmit={handleSubmit(onSubmit)}>
   <QuestionnaireHeaderNew
    heading='Questionário de Diagnóstico ESG'
    isSubmitEnabled={isValid && showPillarsTabs}
    isSubmit={true}
    isSubmitting={isSubmitting}
   >
    <HStack spacing={0}>
        <InputText
        {...register('name')}
        error={errors.name}
        placeholder='Digite o nome do questionário'
        w='340px'
        />
        <YearPickerInput
        value={exercise}
        onChange={(date) => setExercise(date)}
        />
    </HStack>
   </QuestionnaireHeaderNew>

   <Container maxW='1280px' p={0} minH='80vh' my={['8px', '32px']}>
    <Box bg='white' borderRadius='12px' pt='20px'>
     <Tabs index={activeTab} onChange={handleTabsChange} pos='static'>
      <TabList
       fields={fields}
       validPillars={fields.length}
       showPillarsTabs={showPillarsTabs}
       activeTab={activeTab}
       onTabChange={setActiveTab}
      />

      <TabPanels mt='6' borderTop='2px solid #F5F5FB'>
       <PillarConfigList
        setValue={setValue}
        fields={fields}
        control={control}
        isDirty={isDirty}
        handlePillarsConfig={handlePillarsConfig}
        showNumberOfThemes={true}
        showNumberOfLevels={true}
       >
        <Select
         label='Número de pilares'
         placeholder='Selecione o número de pilares'
         options={optionsNumPilares}
         value={pillarCount}
         onChange={(e) => {
          handleNumPilaresChange(parseInt(e.target.value));
          setActiveTab(0);
         }}
         w='279px'
        />
       </PillarConfigList>

       {fields?.map((field, index) => (
        <TabPanel key={field.id} py='0' px='0'>
         <Heading
          fontSize='20px'
          fontWeight={600}
          lineHeight='23.48px'
          p='6'
          borderBottom='2px solid #F5F5FB'
         >
          {field?.pillarName}
         </Heading>

         <PillarForm
          index={index}
          field={field}
          register={register}
          errors={errors}
          control={control}
          trigger={trigger}
          setValue={setValue}
          templates={templates}
          templatesIsLoading={templatesIsLoading}
         />
        </TabPanel>
       ))}
      </TabPanels>
     </Tabs>
    </Box>
   </Container>
   <SuccessModal
    isOpen={isSuccessModalOpen}
    onClose={() => {
     setIsSuccessModalOpen(false);
     navigate('/researches');
    }}
    title='Questionário criado com sucesso!'
   />
  </form>
 );
}
